// Path: ./src/app/pages/PreferenceCuisine/index.tsx
import React, { useState } from "react";
import { Box, Stack } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import { adminApiService } from "../../../utils/api";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { messages } from "../../../locales/messages";
import { toastSuccess } from "../../../utils/pageUtil/toast";
import { TableData } from "../../../utils/interface/tableData";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { SetupPageTopAppBar } from "../../../utils/commonStyledComponents/commonStyled";
import { BlackFontTransparentButton } from "../../../utils/commonStyledComponents/commonMuiStyled";
import { Container, LeftButtonSection, TableWrapper } from "./styled";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { useLoading } from "../../../contextApi/LoadingContext";
import { useCuisineTableColumns } from "./useCuisineTableColumns";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { routes } from "../../../utils/constant/navigationRoutes";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";
import { CuisineRow } from "../../../utils/preference/cuisinePayload";
import { SearchControl } from "../../components/FormKit/formControls";

const PreferenceCuisinePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authHeaders = useAuthHeaders();

  const {
    paginationModel,
    sortModel,
    searchText,
    setQueryParams,
    handleTableChange,
  } = useQueryParams();
  const listGuard = useLatestRequestGuard();

  const [paginationLoading, setPaginationLoading] = useState(false);
  const [cuisineListData, setCuisineListData] =
    useState<TableData<CuisineRow>>();
  const [open, setOpen] = useState(false);
  const [cuisineToBeDeleted, setCuisineToBeDeleted] = useState<CuisineRow>();
  const { showLoadingScreen } = useLoading();

  const handleOpenDelete = (value: CuisineRow) => {
    setCuisineToBeDeleted(value);
    setOpen(true);
  };

  const columns = useCuisineTableColumns({ handleOnDelete: handleOpenDelete });

  const handleCancel = () => {
    setOpen(false);
    setCuisineToBeDeleted(undefined);
  };

  const fetchCuisineList = async () => {
    const filter = searchText ? [`name||$contL||${searchText}`] : undefined;

    await guardedListFetch(
      listGuard,
      setPaginationLoading,
      () =>
        adminApiService.getManyBaseGfPreferenceCuisineAdminControllerGfPreferenceCuisine(
          undefined,
          undefined,
          filter,
          undefined,
          sortModel?.length
            ? [`${sortModel[0].field},${sortModel[0]?.sort?.toUpperCase()}`]
            : ["index,ASC", "name,ASC"],
          ["categories"],
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
          undefined,
          authHeaders,
        ),
      (cuisineList) =>
        setCuisineListData((prev) => ({
          ...prev,
          rows: cuisineList.data as CuisineRow[],
          total: cuisineList?.total,
        })),
    );
  };

  const handleDeleteCuisine = async () => {
    if (!cuisineToBeDeleted?.id) return;

    showLoadingScreen(true);
    const result =
      await adminApiService.deleteOneBaseGfPreferenceCuisineAdminControllerGfPreferenceCuisine(
        cuisineToBeDeleted.id,
        authHeaders,
      );
    showLoadingScreen(false);

    if (result === undefined) return;

    handleCancel();
    toastSuccess(t(messages.Successfully_Deleted()));
    fetchCuisineList();
  };

  React.useEffect(() => {
    setCuisineListData((prev) => ({ ...prev, columns }));
  }, [t]);

  React.useEffect(() => {
    fetchCuisineList();
  }, [searchText, paginationModel, sortModel]);

  return (
    <Container data-testid="preference-cuisine-page-test-id">
      <AccountCardWrapper title={t(messages.preference_cuisines())}>
        <Box data-testid="view-page">
          <SetupPageTopAppBar>
            <LeftButtonSection>
              <Stack direction="row" spacing={2} alignItems="center">
                <PermissionComponentWrapper
                  requiredPermission={["create"]}
                  requiredPermissionGroup={["tag"]}
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <UploadFileIcon
                        style={{ color: "#ff8038", fontSize: 25 }}
                      />
                    }
                    onClick={() => {
                      navigate(routes.preferenceCuisine.create);
                    }}
                    data-testid="create-button"
                  >
                    {t(messages.Create())}
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>

                <SearchControl
                  value={searchText}
                  placeholder={t(messages.preference_cuisine_search())}
                  onSearch={(value) =>
                    setQueryParams({ searchText: value, page: 1 })
                  }
                  dataTestId="preference-cuisine-search"
                />
              </Stack>
            </LeftButtonSection>
          </SetupPageTopAppBar>

          <TableWrapper>
            <ListTableQueryPagination
              loadFailed={listGuard.loadFailed}
              columns={cuisineListData?.columns}
              rows={cuisineListData?.rows}
              total={paginationLoading ? 0 : cuisineListData?.total}
              paginationModel={paginationModel}
              sortModel={sortModel}
              isLoading={paginationLoading}
              onTableChange={handleTableChange}
              dynamicHeightAdjuster={300}
            />
          </TableWrapper>

          <MuiConfirmationModal
            open={open}
            handleCancel={handleCancel}
            handleConfirm={handleDeleteCuisine}
            title={`${t(messages.Delete_Confirmation())}`}
            description={`${t(messages.Are_you_sure_you_want_to_delete_These())}? ${t(
              messages.This_action_cannot_be_undone(),
            )}. ${t(messages.preference_delete_slug_warning(), {
              slug: cuisineToBeDeleted?.slug ?? "",
            })}`}
          />
        </Box>
      </AccountCardWrapper>
    </Container>
  );
};

export default PreferenceCuisinePage;
